/**
 * The appointment wizard's data ports -- the one place the Appointments designer
 * preview and the standalone public booking page differ.
 *
 * They render through the SAME component and the same controller. What changes
 * is only where the settings, the catalogue and the slots come from, and whether
 * a submit is real:
 *
 *   preview  draft settings from the designer's own form; catalogue from the
 *            journey it is editing; availability from the REAL public endpoint;
 *            no booking is ever created.
 *   public   published settings for a slug; catalogue from the same published
 *            config; the same availability endpoint; a real booking.
 *
 * Everything here goes through EXISTING operations. There is no new endpoint and
 * no second facade: requestPublicSurface is the generic public transport the
 * api-client already ships, getPublicJourneyAvailability and
 * bookPublicJourneyAppointment are the existing typed wrappers.
 *
 * Nothing in this module invents data. Where a read returns nothing the caller
 * gets an empty result and shows an empty state -- never a placeholder row. The
 * wizard used to fall back to a baked Lakeview catalogue and to generate slots
 * by arithmetic on the calendar date; on a customer-facing page that is
 * fabricated content, and it is the reason this module exists.
 */
import {
  bookPublicJourneyAppointment,
  getPublicJourneyAvailability,
  requestPublicSurface,
} from "@v0y4ge/api-client";
import { studioRequestOptions } from "../../lib/studio-config";

export type AppointmentMode = "preview" | "public";

export interface PublicSlot {
  id: string;
  time: string;
  startUtc: string;
  endUtc: string;
  staffId: string;
  staffName: string | null;
}

/** One booking question as the forms creator authored it, resolved server-side
 *  from the service's question group. Rendered at the details step. */
export interface BookingQuestion {
  id: string; label: string; type: string; help: string; required: boolean;
  options: { id: string; label: string }[]; groupId: string | null; groupName: string | null;
}

export interface AppointmentCatalog {
  services: { id: string; name: string; desc: string; cat: string; dur: string; price: string; window: number; locs: string[]; questions: BookingQuestion[] }[];
  locations: { id: string; name: string; addr: string; dist: string; tz: string; next: string; hours: string; phone: string }[];
  staff: { id: string; name: string }[];
  durations: { id: string; label: string; sub: string }[];
}

export const EMPTY_CATALOG: AppointmentCatalog = { services: [], locations: [], staff: [], durations: [] };

/**
 * Where an explicit-base public read points.
 *
 * Studio is served from a host that injects its own configured API base, and it
 * already carries the tenant prefix -- studioRequestOptions() is that base and
 * is the only supported way to reach it. The standalone booking bundle is served
 * same-origin with its API through the gateway, so an empty base plus host
 * tenant routing is correct there.
 *
 * Deliberately NOT a call to configureApiClient. The shared component runs
 * inside Studio, and reconfiguring the global client from it would repoint every
 * other screen's requests.
 */
export function publicReadOptions(mode: AppointmentMode): { baseUrl: string } {
  return mode === "public" ? { baseUrl: "" } : { baseUrl: studioRequestOptions().baseUrl };
}

/** The slug a standalone booking page is serving, from the first path segment. */
export function slugFromPathname(pathname: string): string {
  const parts = String(pathname || "").split("/").filter(Boolean);
  return parts.length > 0 ? decodeURIComponent(parts[0]) : "";
}

type Dict = Record<string, any>;

/**
 * The published surface for a slug: configJson, status, isLive, slug and the
 * rest. Returns null when the journey does not exist or is not published -- the
 * caller renders a content-free not-found rather than anything naming the
 * tenant.
 */
export async function loadPublishedJourney(slug: string, mode: AppointmentMode): Promise<Dict | null> {
  if (!slug) return null;
  const surface = await requestPublicSurface<Dict>(
    "/v1/journeys/public/" + encodeURIComponent(slug),
    { method: "GET" },
    publicReadOptions(mode),
    true,
  );
  return surface ?? null;
}

function minutesLabel(minutes: unknown): string {
  const n = Math.round(Number(minutes) || 0);
  if (!n) return "";
  if (n < 60) return n + " min";
  const h = Math.floor(n / 60);
  const m = n % 60;
  return m ? h + " hour " + m + " minutes" : h + " hour" + (h > 1 ? "s" : "");
}

/**
 * MAJOR units in, formatted string out.
 *
 * The unit is in the parameter name because getting it wrong is invisible: the
 * public catalogue publishes `price` ALREADY in major units -- journey-runtime's
 * catalog.ts priceField() divides the stored minor value by 100 before it is
 * serialised -- and publishes no `priceMinor` at all. Only the admin shape
 * carries minor units. Formatting both through one minor-unit helper rendered
 * every public price at a hundredth of its value, so a $50.00 service read
 * $0.50 on the customer's booking page.
 *
 * The sibling formatter in alljourneys-catalog.ts takes major units too; these
 * two must not disagree about the unit again.
 */
function priceLabel(major: unknown, currency: unknown): string {
  const n = Number(major);
  if (!Number.isFinite(n) || n <= 0) return "";
  const code = String(currency || "USD");
  const amount = n.toFixed(2);
  return code === "USD" ? "$" + amount : amount + " " + code;
}

/**
 * The catalogue the wizard draws, taken from the journey's OWN published config.
 *
 * configJson.custom carries the tenant's real hydrated services, locations and
 * staff -- the same rows the designer's admin reads return -- so one published
 * read serves both surfaces and they cannot disagree. The public /catalog
 * endpoint is deliberately not used for locations: it returns none, and the
 * branches a journey offers are a property of the journey's own config.
 */
/**
 * The catalogue a public visitor can actually be shown.
 *
 * NOT built from configJson.custom. The admin journey row carries hydrated
 * services, locations and staff there, and it is tempting to reuse -- but the
 * public surface runs the stored config through normalizeJourneyConfig, which
 * keeps only declared contract fields, so `custom` is absent from every public
 * response. Verified against the live tenant: the public payload has no custom
 * block at all, while the admin row has three services, two locations and two
 * staff in it.
 *
 * So each part comes from where the contract actually publishes it:
 *   services   the public /catalog read
 *   staff      the journey's own published staff roster
 *   locations  the journey's published branches
 *
 *   locations  the public /catalog read, already resolved by the server:
 *              { id, name, address?, timezone?, virtual }, with mode and
 *              pinVirtual applied server-side. The list is FINAL -- do not
 *              re-filter it here, or the two surfaces stop agreeing.
 *
 * Distance and next-available are absent by agreement: both need a live
 * calculation the catalogue cannot do, so they stay empty rather than being
 * filled with something plausible.
 */
export function buildCatalog(
  config: Dict | null | undefined,
  catalogServices: Dict[],
  catalogLocations: Dict[],
  availabilityStaff: { id: string; name: string }[],
): AppointmentCatalog {
  const publishedStaff = (config && (config as Dict).staff) || {};
  // The server's final list. This used to read the journey's own
  // `steps.location.branches`, which is empty on a journey whose branches were
  // never published -- exactly the live case -- while the server-rendered page
  // fell back to the tenant's locations and showed two. Reading the resolved
  // list is what closes that gap rather than reproducing the fallback here.
  const rawLocations: Dict[] = Array.isArray(catalogLocations) ? catalogLocations : [];
  const rawServices: Dict[] = Array.isArray(catalogServices) ? catalogServices : [];
  const rosterStaff: Dict[] = Array.isArray(publishedStaff.roster) ? publishedStaff.roster : [];
  const rawStaff: Dict[] = rosterStaff.length ? rosterStaff : availabilityStaff;

  const locations = rawLocations
    .filter((row) => row && row.id)
    .map((row) => ({
      id: String(row.id),
      // `name`, matching the tenant-row spelling the payload uses. A service is
      // the one that carries `title`.
      name: String(row.name || ""),
      // ONE composed line, composed server-side by the same helpers the booking
      // confirmation uses. Re-composing it here from parts is how the card a
      // visitor picks and the confirmation they receive end up disagreeing about
      // one place.
      //
      // A virtual branch is labelled from the FLAG, never from an empty address:
      // an addressless branch arrives as the literal "GB", because the platform's
      // address normaliser defaults country to GB and composes from the parts. A
      // fallback keyed on emptiness would never fire, and the card would print
      // "GB" as the venue.
      addr: row.virtual === true ? "Virtual appointment" : String(row.address || ""),
      // Distance needs the visitor's position, and the next free slot needs an
      // availability read per branch. Neither is known here, and inventing
      // either is what this module exists to stop.
      dist: "",
      tz: String(row.timezone || ""),
      next: "",
      hours: String(row.hours || ""),
      phone: String(row.phone || ""),
    }));

  // A branch offers a service when it carries that service's line of business.
  // Absent that mapping the service is offered everywhere, which is what the
  // journey's own step does when no branch filter applies.
  const locsFor = (service: Dict): string[] => {
    const lob = String(service.lob || service.category || "");
    if (!lob) return locations.map((row) => row.id);
    const matching = rawLocations
      .filter((row) => Array.isArray(row.lobs) && row.lobs.indexOf(lob) > -1)
      .map((row) => String(row.id));
    // A branch that names no lines of business offers everything, which is what
    // the server-rendered page does with the same data.
    return matching.length ? matching : locations.map((row) => row.id);
  };

  const services = rawServices
    .filter((row) => row && row.id)
    .map((row) => ({
      id: String(row.id),
      // The public catalogue names a service `title`; the admin row calls the
      // same field `name`.
      name: String(row.title || row.name || ""),
      desc: String(row.description || ""),
      cat: String(row.category || row.lob || "").trim(),
      // `detail` is already a rendered duration ("30 min") on the public
      // catalogue; only the admin shape carries raw minutes.
      dur: String(row.detail || "") || minutesLabel(row.duration),
      // Both shapes normalised to MAJOR units before formatting: the admin row
      // carries minor units under `priceMinor`, the public catalogue carries major
      // units under `price`.
      price: priceLabel(row.priceMinor != null ? Number(row.priceMinor) / 100 : row.price, row.currency),
      // The config carries no per-service booking window; the availability read
      // is the authority on what is bookable, so this only bounds the calendar.
      window: 60,
      locs: locsFor(row),
      // The service's booking questions, resolved by the catalogue read (seed
      // review step 06). Absent on an older worker → no extra fields, never invented.
      questions: Array.isArray(row.bookingQuestions) ? (row.bookingQuestions as BookingQuestion[]) : [],
    }));

  const staff = rawStaff
    .filter((row) => row && row.id)
    .map((row) => ({ id: String(row.id), name: String(row.name || "") }));

  const durationLabels: string[] = [];
  for (const row of rawServices) {
    for (const entry of (Array.isArray(row.durations) ? row.durations : [])) {
      const label = String((entry && entry.duration) || "");
      if (label && durationLabels.indexOf(label) === -1) durationLabels.push(label);
    }
  }
  const durations = durationLabels.slice(0, 4).map((label, index) => ({ id: "d" + index, label, sub: "" }));

  return { services, locations, staff, durations };
}

export interface PublicCatalogRead {
  services: Dict[];
  /**
   * One already-normalised list. The SERVER resolves which source it came from
   * -- the journey's published branches when it has them, otherwise the
   * tenant's public locations -- and the client is deliberately not told which.
   * Knowing would change nothing that renders, and a public payload should not
   * widen to make a test easier to write.
   */
  locations: Dict[];
}

/**
 * The public catalogue for a slug: what a visitor may be offered, and where.
 *
 * Returns **null** when there was no catalogue to read — no slug, or a journey
 * that is missing or unpublished. That is deliberately distinct from a payload
 * whose arrays are empty, which means the tenant genuinely offers nothing. A
 * caller must be able to tell "nothing published" from "the read did not
 * happen", because only the first should replace what is already on screen.
 */
export async function loadPublicCatalog(slug: string, mode: AppointmentMode): Promise<PublicCatalogRead | null> {
  if (!slug) return null;
  const payload = await requestPublicSurface<Dict>(
    "/v1/journeys/public/" + encodeURIComponent(slug) + "/catalog",
    { method: "GET" },
    publicReadOptions(mode),
    true,
  );
  if (!payload) return null;
  const services = payload.services;
  const locations = payload.locations;
  return {
    services: Array.isArray(services) ? services : [],
    // An older worker omits `locations` entirely. That reads as "none
    // published" — never as an error, and never as a reason to substitute rows.
    locations: Array.isArray(locations) ? locations : [],
  };
}

export interface AvailabilityWindow {
  /** ISO date to the real slots the backend offered for it. Never generated. */
  byDay: Record<string, PublicSlot[]>;
  staff: { id: string; name: string }[];
  selectedStaffId: string | null;
  /** The key the window was loaded for, so a stale response can be discarded. */
  key: string;
}

export const EMPTY_AVAILABILITY: AvailabilityWindow = { byDay: {}, staff: [], selectedStaffId: null, key: "" };

function isoDate(d: Date): string {
  return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
}

export function availabilityKey(slug: string, serviceId: string, staffId: string, from: string, to: string): string {
  return [slug, serviceId, staffId || "", from, to].join("|");
}

/**
 * Real availability for one service over a date window.
 *
 * Each slot is carried through WHOLE -- startUtc, endUtc and staffId are the
 * backend's own and are submitted unchanged. A displayed time is never parsed
 * back into an instant: that is how a booking lands an hour out across a
 * daylight-saving boundary.
 */
export async function loadAvailability(params: {
  slug: string;
  serviceId: string;
  staffId?: string;
  daysAhead?: number;
}): Promise<AvailabilityWindow> {
  const slug = params.slug;
  const serviceId = params.serviceId;
  if (!slug || !serviceId) return EMPTY_AVAILABILITY;
  const today = new Date();
  const from = isoDate(today);
  const to = isoDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() + (params.daysAhead || 60)));
  const key = availabilityKey(slug, serviceId, params.staffId || "", from, to);

  const query: Dict = { serviceId: serviceId, dateFrom: from, dateTo: to };
  if (params.staffId) query.staffId = params.staffId;
  const response = (await getPublicJourneyAvailability(slug, query as any)) as Dict;

  const payload = (response && (response as Dict).data) || {};
  const byDay: Record<string, PublicSlot[]> = {};
  const days: Dict[] = Array.isArray(payload.days) ? payload.days : [];
  for (const day of days) {
    const date = String(day && day.date ? day.date : "");
    if (!date) continue;
    const slots: Dict[] = Array.isArray(day.slots) ? day.slots : [];
    byDay[date] = slots
      .map((slot) => ({
        id: String(slot.id || slot.slotId || ""),
        time: String(slot.time || ""),
        startUtc: String(slot.startUtc || slot.start_utc || ""),
        endUtc: String(slot.endUtc || slot.end_utc || ""),
        staffId: String(slot.staffId || slot.staff_id || ""),
        staffName: slot.staffName != null ? slot.staffName : (slot.staff_name != null ? slot.staff_name : null),
      }))
      .filter((slot) => slot.startUtc && slot.endUtc);
  }

  const staffRows: Dict[] = Array.isArray(payload.staff) ? payload.staff : [];
  return {
    byDay: byDay,
    staff: staffRows.filter((row) => row && row.id).map((row) => ({ id: String(row.id), name: String(row.name || "") })),
    selectedStaffId: payload.selectedStaffId ? String(payload.selectedStaffId) : null,
    key: key,
  };
}

/** Which part of the day a real slot belongs to, for the grouped slot picker. */
export function partOfDay(slot: PublicSlot): "Morning" | "Afternoon" | "Evening" {
  const match = /^(\d{1,2}):(\d{2})\s*(AM|PM)?/i.exec(slot.time || "");
  let hour: number;
  if (match) {
    hour = parseInt(match[1], 10);
    // The availability endpoint returns 24-hour times ("09:00", "14:00"). Only a
    // meridiem-bearing value may be folded onto a 12-hour clock; doing it
    // unconditionally put every afternoon slot in the Morning group.
    const meridiem = (match[3] || "").toLowerCase();
    if (meridiem) {
      hour = hour % 12;
      if (meridiem === "pm") hour += 12;
    }
  } else {
    const parsed = new Date(slot.startUtc);
    hour = Number.isNaN(parsed.getTime()) ? 12 : parsed.getHours();
  }
  if (hour < 12) return "Morning";
  if (hour < 17) return "Afternoon";
  return "Evening";
}

export interface BookingRequest {
  slug: string;
  serviceId: string;
  locationId?: string;
  slot: PublicSlot;
  guest: { firstName?: string; lastName?: string; email: string; phone?: string };
  answers?: Record<string, unknown>;
  notificationPreference?: string;
}

/**
 * Submit a real booking. PREVIEW NEVER CALLS THIS -- the designer's confirmation
 * step is a rendering of settings, and a preview that created a customer record
 * would be indistinguishable from a real one in the tenant's own data.
 */
export async function submitBooking(request: BookingRequest): Promise<Dict> {
  const slot = request.slot;
  const input: Dict = {
    serviceId: request.serviceId,
    // The slot's own staff identity, unchanged. The customer may never have
    // chosen a person, but the instant they picked belongs to one.
    staffId: slot.staffId,
    slotStartUtc: slot.startUtc,
    slotEndUtc: slot.endUtc,
    guest: request.guest,
    answers: request.answers || {},
    // (answers carry the details-step responses to the service's booking questions)
    agreedToTerms: true,
  };
  if (request.locationId) input.locationId = request.locationId;
  if (request.notificationPreference) input.notificationPreference = request.notificationPreference;
  return (await bookPublicJourneyAppointment(request.slug, input as any)) as Dict;
}

/** Read a booking back, which is what proves it exists rather than 2xx alone. */
export async function readBooking(
  slug: string,
  bookingId: string,
  email: string,
  mode: AppointmentMode,
): Promise<Dict | null> {
  if (!slug || !bookingId || !email) return null;
  const path = "/v1/journeys/public/" + encodeURIComponent(slug)
    + "/bookings/" + encodeURIComponent(bookingId)
    + "?email=" + encodeURIComponent(email);
  return await requestPublicSurface<Dict>(path, { method: "GET" }, publicReadOptions(mode), true);
}
